import * as Api from '@ViewModels';
import { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import {
	invalidateInfiniteSurveys,
	invalidateSurveyById,
	useCreateCustomSurveyMutation,
	useReorderCustomSurveyFieldsMutation,
	useUpdateCustomSurveyMutation,
} from '../../../../../queries';
import {
	invalidateSystemSurveyTemplateQuery,
	useReorderSurveyTemplateFieldsMutation,
} from '../../../../../queries/SurveyTemplate';
import { FieldStateManager } from '../../shared/components/AdditionalQuestions/types';
import { patchFieldTypesFromServerResponse } from '../../shared/utils/patchFieldTypesFromServerResponse';
import { CustomSurveyFormData } from '../utils';

interface UseCustomSurveyMutationsProps {
	impersonationContext?: Api.IImpersonationContext;
	getValues: UseFormGetValues<CustomSurveyFormData>;
	reset: UseFormReset<CustomSurveyFormData>;
	setValue: UseFormSetValue<CustomSurveyFormData>;
	surveyData?: Api.ICustomSurvey;
	onReorderSuccess?: (fields: Api.IFormField<string>[]) => void;
	/** When true, uses admin template reorder mutation instead of survey reorder mutation */
	templateMode?: boolean;
	/** Template ID when in template mode - required for invalidating template queries */
	templateId?: string;
	/** Lifted per-field dirty manager — cleared after a successful save. */
	fieldStateManager?: FieldStateManager;
}

// Hook for mutations
export const useCustomSurveyMutations = ({
	impersonationContext,
	getValues,
	reset,
	setValue,
	onReorderSuccess,
	templateMode = false,
	templateId,
	fieldStateManager,
}: UseCustomSurveyMutationsProps) => {
	const history = useHistory();
	const { logApiError } = useEventLogging('CustomSurvey');
	const errorMessages = useErrorMessages();

	// Step 1: Update existing survey
	const updateSurveyMutation = useUpdateCustomSurveyMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update the custom survey');
			logApiError('CustomUpdateSurveySave-Error', error);
		},
		onSuccess: (data?: Api.ICustomSurvey) => {
			toast.success('Custom survey updated successfully');
			invalidateSurveyById();
			invalidateInfiniteSurveys();
			// Patch `_type` onto locally-added fields so `hasBeenSaved` flips to true and the
			// "Not Saved" indicator clears. We deliberately do NOT replace the field array
			// wholesale — the save response strips `options` (those come back via the GET's
			// `?expand=options`), so a blanket overwrite wipes dropdown choices and breaks
			// any visibility rules that reference them.
			patchFieldTypesFromServerResponse(data?.customForm?.fields, getValues().customForm?.fields, setValue);
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			fieldStateManager?.resetDirtyState();
			fieldStateManager?.updateInitialStates(formValues.customForm?.fields ?? []);
		},
	});

	// Step 1: Create new survey
	const createSurveyMutation = useCreateCustomSurveyMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create custom survey');
			logApiError('CustomCreateSurvey-Error', error);
		},
		onSuccess: (val: Api.ICustomSurvey) => {
			toast.success('Custom survey created');
			history.replace(`/surveys/custom/edit/${val.id}`);
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// See update-onSuccess for why we patch rather than overwrite.
			patchFieldTypesFromServerResponse(val?.customForm?.fields, getValues().customForm?.fields, setValue);
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			fieldStateManager?.resetDirtyState();
			fieldStateManager?.updateInitialStates(formValues.customForm?.fields ?? []);
		},
	});

	// Reorder survey fields - regular survey mode
	const reorderSurveyFieldsMutation = useReorderCustomSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder custom survey fields');
			logApiError('CustomReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Custom survey fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
		},
	});

	// Reorder template fields - template mode
	const reorderTemplateFieldsMutation = useReorderSurveyTemplateFieldsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder template fields');
			logApiError('TemplateReorderFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Template fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
			// Invalidate template query to refetch with new field order
			if (templateId) {
				invalidateSystemSurveyTemplateQuery(templateId);
			}
		},
	});

	return {
		updateSurveyMutation,
		createSurveyMutation,
		reorderSurveyFieldsMutation,
		reorderTemplateFieldsMutation,
		templateMode,
	};
};
